import Skeleton from '@mui/material/Skeleton';
import moment from 'moment';
import { type JSX, useEffect, useMemo, useState } from 'react';

import { BarChart } from 'Components/BaseCharts/BarChart';
import { CSSColors } from 'Components/Colors';
import { FormFieldDateOptionSelect } from 'Components/FormField/FormFieldDateOptionSelect/FormFieldDateOptionSelect';
import { ChangeEventType } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldSelectDark } from 'Components/FormField/FormFieldSelectDark/FormFieldSelectDark';
import { CustomDateRangeModal } from 'Components/Modal/CustomDateRangeModal/CustomDateRangeModal';
import { Text } from 'Components/Text/Text';
import { toTitleCase } from 'Helpers/StringUtils';
import { useDateFilter } from 'Hooks/DateFilter';
import { DateOptionSelectOption, DefaultUpcomingDateRangeOptions, IssuesExceptionsDashboardType } from 'Models/Dashboards';
import { IssuePriority, IssuePriorityFilter, IssuePriorityFilterOptions, IssueResponse, IssueStatus, isValidIssuePriorityFilter } from 'Models/Issues';
import { OrganizationResponseBase } from 'Models/Organization';
import { getMonths, getOpenIssuesPerMonth, getProductModuleForDashboardType } from 'Pages/Dashboards/Helpers/Dashboards.helpers';

import styles from './DashboardsTrackingIssues.module.css';
import { TrackingIssuesDisplayModal } from './TrackingIssuesDisplayModal/TrackingIssuesDisplayModal';

export interface DashboardsTrackingIssuesProps {
    allIssues?: IssueResponse[];
    dashboardType: IssuesExceptionsDashboardType;
    issuesError?: string;
    organizations?: OrganizationResponseBase[];
    organizationsError?: string;
}

enum Modals {
    NONE,
    DISPLAY_OPEN_ISSUES,
    CUSTOM_DATE,
}

/**
 * Renders a bar chart that displays, over a given time period, and grouped by month, the number of issues that are coming due.
 * The first bar shows overdue issues.
 */
export const DashboardsTrackingIssues = (props: DashboardsTrackingIssuesProps): JSX.Element => {
    const dashboardIssues = useMemo(() => {
        if (!props.allIssues) return undefined;
        const moduleType = getProductModuleForDashboardType(props.dashboardType);
        return props.allIssues.filter((issue) => issue.type === moduleType && issue.status === IssueStatus.OPEN);
    }, [props.allIssues, props.dashboardType]);

    const [issuesByMonth, setIssuesByMonth] = useState<number[]>();
    const [monthLabel, setMonthLabel] = useState<string[]>();
    const [clickedIssues, setClickedIssue] = useState<IssueResponse[]>();
    const [modalState, setModalState] = useState<Modals>(Modals.NONE);
    const [clickedDate, setClickedDate] = useState<string>();
    const [issueFilterType, setIssueFilterType] = useState<IssuePriorityFilter>(IssuePriorityFilter.ALL);

    const dateFilterHookValues = useDateFilter(DateOptionSelectOption.NEXT_THREE_MONTHS);

    useEffect(() => {
        const issuesPriorityFilter = (issue: IssueResponse): boolean => {
            if (issueFilterType === IssuePriorityFilter.ALL) {
                return true;
            } else if (issueFilterType === IssuePriorityFilter.HIGH) {
                return issue.priority === IssuePriority.HIGH;
            } else if (issueFilterType === IssuePriorityFilter.MEDIUM) {
                return issue.priority === IssuePriority.MEDIUM;
            } else if (issueFilterType === IssuePriorityFilter.LOW) {
                return issue.priority === IssuePriority.LOW;
            } else {
                return true;
            }
        };
        if (dashboardIssues) {
            const issuesByPriority = dashboardIssues.filter(issuesPriorityFilter);

            const months = getMonths(dateFilterHookValues.selection.dateRange.startDate, dateFilterHookValues.selection.dateRange.endDate);

            const totalIssuesByMonth = months.map((month) => {
                return issuesByPriority.filter((issue) => moment(issue.due_date).format('MMM YY') === month).length;
            });

            const overDueIssues = issuesByPriority.filter((issue) => moment(issue.due_date).isBefore(moment()));

            setIssuesByMonth([overDueIssues.length, ...totalIssuesByMonth]);
            setMonthLabel(['Overdue', ...months]);
        }
    }, [dashboardIssues, issueFilterType, dateFilterHookValues]);

    const handleChartClick = (item: string) => {
        const currentFilteredIssues = dashboardIssues?.filter((issue) => {
            if (issueFilterType === IssuePriorityFilter.ALL) {
                return true;
            } else if (issueFilterType === IssuePriorityFilter.HIGH) {
                return issue.priority === IssuePriority.HIGH;
            } else if (issueFilterType === IssuePriorityFilter.MEDIUM) {
                return issue.priority === IssuePriority.MEDIUM;
            } else if (issueFilterType === IssuePriorityFilter.LOW) {
                return issue.priority === IssuePriority.LOW;
            } else {
                return true;
            }
        });

        if (currentFilteredIssues) {
            const clickedItem = item.split(' | ');
            const clickedDate = toTitleCase(clickedItem[0]);
            if (clickedDate === 'Overdue') {
                const overdueIssues = currentFilteredIssues.filter((issue) => moment(issue.due_date).isBefore(moment()));

                setClickedIssue(overdueIssues);
            } else {
                setClickedIssue(getOpenIssuesPerMonth(currentFilteredIssues, clickedDate));
            }
            setClickedDate(clickedDate);
            setModalState(Modals.DISPLAY_OPEN_ISSUES);
        }
    };

    const handleRiskTypeChange = (value: ChangeEventType, formFieldId: string) => {
        if (isValidIssuePriorityFilter(value)) {
            setIssueFilterType(value as IssuePriorityFilter);
        }
    };

    if (props.issuesError) {
        return (
            <>
                <div className={styles.cellHeader}>
                    <Text variant="Header2" color="white">
                        Issue Tracking
                    </Text>
                </div>
                <Text color="white" variant="Header4">
                    {props.issuesError}
                </Text>
            </>
        );
    }

    if (props.organizationsError) {
        return (
            <>
                <div className={styles.cellHeader}>
                    <Text variant="Header2" color="white">
                        Issue Tracking
                    </Text>
                </div>
                <Text color="white" variant="Header4">
                    {props.organizationsError}
                </Text>
            </>
        );
    }

    return (
        <>
            {modalState === Modals.CUSTOM_DATE && <CustomDateRangeModal datesRestriction="futureDatesOnly" hideModal={() => setModalState(Modals.NONE)} dateFilterHookValues={dateFilterHookValues} />}
            {modalState === Modals.DISPLAY_OPEN_ISSUES && clickedDate && clickedIssues && <TrackingIssuesDisplayModal selectedIssue={clickedIssues} date={clickedDate} hideModal={() => setModalState(Modals.NONE)} dashboardType={props.dashboardType} organizations={props.organizations} />}
            <div className={styles.cellHeader}>
                <Text variant="Header2" color="white">
                    Issue Tracking
                </Text>
                <div className={styles.filters}>
                    <FormFieldSelectDark formFieldId="issuePriority" formFieldLabel="Priority..." options={IssuePriorityFilterOptions} selectedOption={issueFilterType} handleChange={handleRiskTypeChange} />
                    <FormFieldDateOptionSelect formFieldId="date" options={DefaultUpcomingDateRangeOptions} onSelectCustom={() => setModalState(Modals.CUSTOM_DATE)} dateFilterHookValues={dateFilterHookValues} />
                </div>
            </div>
            {monthLabel && issuesByMonth ? <BarChart onChartClick={handleChartClick} xAxisLabels={monthLabel} bars={{ name: 'Total Due', color: CSSColors.LIGHT_BLUE, data: issuesByMonth }} yAxisInfo={[{ name: 'Open Issues By Month', position: 'left', minInterval: 1 }]} /> : <Skeleton variant="rounded" width={'100%'} height={300} />}
        </>
    );
};
